import React from 'react';

import {
  IconActivityHeartbeat,
  IconCalendarMonth,
  IconChartAreaLine,
  IconFileDollar,
  IconFileSearch,
  IconHierarchy,
  IconListSearch,
  IconPrinter,
  IconReceipt,
  IconUserPlus,
} from '@tabler/icons-react';
import _ from 'lodash';

import { Capability, hasAnyCapability } from '@repo/utils/auth';
import {
  ADMIN_INVOICES_HREF,
  ADMIN_ONBOARDING_PREVIEW_HREF,
  ADMIN_PAYROLL_HREF,
  ADMIN_PRINTING_HREF,
  ADMIN_TIMESHEET_HREF,
  ANALYTICS_HREF,
  APPS_HREF,
  NILE_QUESTIONS_HREF,
  NILE_SOURCES_HREF,
  NILE_TAXONOMY_HREF,
  STUDENT_ACTIVITY_LOG_HREF,
} from '../../utils/common/hrefs';

// The gallery's sections, in the order it shows them.
export const APP_SECTIONS = ['Admin', 'Analytics', 'Nile'] as const;

export type AppSection = (typeof APP_SECTIONS)[number];

// One entry in the Apps gallery: a page reached often enough to need a home,
// but not often enough to hold a place in the sidebar.
export interface AppOption {
  icon: React.ReactElement;
  name: string;
  href: string;
  // Endpoints the app owns, so the sidebar's Apps entry stays selected while
  // the reader is inside one.
  linkedEndpoints: string[];
  // Shown to a caller holding any of these, matching the gate on the app's own route
  capabilities: Capability[];
  section: AppSection;
}

export interface AppSectionGroup {
  section: AppSection;
  apps: AppOption[];
}

const APPS: AppOption[] = [
  {
    icon: <IconReceipt />,
    name: 'Invoices',
    href: ADMIN_INVOICES_HREF,
    linkedEndpoints: ['admin/invoices'],
    capabilities: [Capability.InvoicesView],
    section: 'Admin',
  },
  {
    icon: <IconFileDollar />,
    name: 'Payroll',
    href: ADMIN_PAYROLL_HREF,
    linkedEndpoints: ['admin/payroll'],
    capabilities: [Capability.PayrollView],
    section: 'Admin',
  },
  {
    icon: <IconPrinter />,
    name: 'Printing',
    href: ADMIN_PRINTING_HREF,
    linkedEndpoints: ['admin/printing'],
    capabilities: [Capability.PrintingUse],
    section: 'Admin',
  },
  {
    icon: <IconCalendarMonth />,
    name: 'Timesheet',
    href: ADMIN_TIMESHEET_HREF,
    linkedEndpoints: ['admin/timesheet'],
    capabilities: [Capability.TimesheetView],
    section: 'Admin',
  },
  {
    icon: <IconUserPlus />,
    name: 'Student onboarding',
    href: ADMIN_ONBOARDING_PREVIEW_HREF,
    linkedEndpoints: ['admin/onboarding-preview'],
    capabilities: [Capability.OnboardingPreview],
    section: 'Admin',
  },
  {
    icon: <IconActivityHeartbeat />,
    name: 'Activity log',
    href: STUDENT_ACTIVITY_LOG_HREF,
    linkedEndpoints: ['student-activity-log'],
    capabilities: [Capability.ActivityLogViewAll, Capability.ActivityLogViewOwn],
    section: 'Analytics',
  },
  {
    icon: <IconChartAreaLine />,
    name: 'Student analytics',
    href: ANALYTICS_HREF,
    linkedEndpoints: ['analytics'],
    capabilities: [Capability.StudentAnalyticsView],
    section: 'Analytics',
  },
  {
    icon: <IconListSearch />,
    name: 'Nile questions',
    href: NILE_QUESTIONS_HREF,
    linkedEndpoints: ['nile/questions'],
    capabilities: [Capability.NileQuestionsView],
    section: 'Nile',
  },
  {
    icon: <IconFileSearch />,
    name: 'Nile sources',
    href: NILE_SOURCES_HREF,
    linkedEndpoints: ['nile/sources'],
    capabilities: [Capability.NileSourcesView],
    section: 'Nile',
  },
  {
    icon: <IconHierarchy />,
    name: 'Nile taxonomy',
    href: NILE_TAXONOMY_HREF,
    linkedEndpoints: ['nile/taxonomy'],
    capabilities: [Capability.NileTaxonomyView],
    section: 'Nile',
  },
];

// Every capability with an app behind it, which the gallery's route is gated on.
export const APP_CAPABILITIES: Capability[] = _.uniq(APPS.flatMap(app => app.capabilities));

// The apps a caller may open, in gallery order: by section, then by name.
export const getAppOptions = (capabilities: readonly Capability[]): AppOption[] =>
  _.sortBy(
    APPS.filter(app => hasAnyCapability(capabilities, app.capabilities)),
    app => APP_SECTIONS.indexOf(app.section),
    app => app.name.toLowerCase()
  );

// Groups apps under their sections in gallery order, leaving out any section
// with none of them.
export const groupAppsBySection = (apps: AppOption[]): AppSectionGroup[] =>
  APP_SECTIONS.map(section => ({
    section,
    apps: apps.filter(app => app.section === section),
  })).filter(group => group.apps.length > 0);

// Every endpoint an app owns, so the sidebar's Apps entry highlights on all of
// them rather than only on the gallery itself.
export const getAppEndpoints = (): string[] => [
  APPS_HREF.slice(1),
  ...APPS.flatMap(app => app.linkedEndpoints),
];
